import { useMemo } from 'react'
import type { FadingScrollableBottomChrome } from '@renderer/chat'
import { useSidepanelFloatingBarHeight } from '../atoms'

/**
 * How far the fade extends above the toolbar (or the scroller's bottom edge
 * when there is no bar). `View.Body` uses this for `bottomInset="fade"`.
 */
export const VIEW_BOTTOM_FADE_SPAN_PX = 72

/** Breathing room under the last row of a list — the fade is part of that look. */
const LIST_BOTTOM_INSET_PX = 24

/**
 * How much bottom padding a view body needs so the last of the content can
 * scroll clear of the floating toolbar and its dissolve. The composer is in
 * document flow and does not belong in this calculation.
 *
 * `View.Body` applies this itself; call it directly only when the body
 * can't be a `View.Body` (the Action View's editable `FadingScrollable`),
 * so the arithmetic still has exactly one owner.
 */
export function useViewBodyBottomInset(bottomInset: 'fade' | 'list' = 'fade'): number {
  const floatingBarHeight = useSidepanelFloatingBarHeight()
  return floatingBarHeight + (bottomInset === 'fade' ? VIEW_BOTTOM_FADE_SPAN_PX : LIST_BOTTOM_INSET_PX)
}

/**
 * The floating toolbar as a view body's `FadingScrollable` sees it: transparent
 * under the bar, dissolving for the fade span above it. Same ownership rule as
 * the inset. `View.Body` applies it, and only a body that can't be one reads it
 * directly.
 */
export function useViewBodyBottomChrome(): FadingScrollableBottomChrome {
  const floatingBarHeight = useSidepanelFloatingBarHeight()
  return useMemo(() => ({ inset: floatingBarHeight, fadeSize: VIEW_BOTTOM_FADE_SPAN_PX }), [floatingBarHeight])
}
